import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Eye, EyeOff, Building2, User } from "lucide-react";
import { useFornitore, urlLegali } from "../utils/fornitore";
import { LISTINO, canoneMensile, euro } from "../utils/pianiNexiora";
import { erroreFormatoPiva, normalizzaPiva } from "../utils/pivaOrganizzazione";
import { erroreNuovaPassword, PASSWORD_MIN } from "../utils/regolePassword";

export default function LoginPage() {
  // Ponte dal link nell'email di invito — stesso principio del ponte dalla demo qui sotto:
  // se il link contiene ?invito=CODICE, si apre già su "Registrati come membro" con il
  // codice già scritto, invece di lasciare la persona a copiarlo a mano dall'email.
  const invitoDaURL = new URLSearchParams(window.location.search).get("invito") || "";

  const [mode, setMode] = useState(() => {
    if (invitoDaURL) return "register-member";
    // Ponte dal sito di presentazione (e da qualunque link esterno) — ?mode=registra apre
    // subito il modulo di registrazione azienda, invece di lasciare la persona scegliere
    // da una schermata di login generica: chi ha già cliccato "Registra la tua azienda"
    // altrove non deve più rifarlo qui.
    if (new URLSearchParams(window.location.search).get("mode") === "registra") return "register-org";
    // Ponte dalla demo pubblica — se il visitatore ha appena cliccato "Registra la tua
    // azienda", si apre già sulla registrazione vera, non sul login generico.
    if (sessionStorage.getItem("nexiora_apri_registrazione") === "1") {
      sessionStorage.removeItem("nexiora_apri_registrazione");
      return "register-org";
    }
    return "login";
  }); // login | register-org | register-member
  const [form, setForm] = useState({ email:"", password:"", nome:"", cognome:"", orgName:"", piva:"", indirizzo:"", phone:"", confirmPassword:"", inviteCode: invitoDaURL });
  // Accettazioni legali (versione = data delle Condizioni/Informativa pubblicate su nexiora.it)
  const VERSIONE_DOCUMENTI_LEGALI = "2026-10-30";
  const [accetta, setAccetta] = useState({ condizioni:false, clausole:false, privacy:false });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invitoInfo, setInvitoInfo] = useState(null); // {orgName, invitatoDaNome} — solo se arrivati da un link valido
  const [invitoCaricato, setInvitoCaricato] = useState(!invitoDaURL);
  const { login, registerOrganization, registerMember, resetPassword, verificaPivaRegistrazione, richiediAccesso } = useAuth();
  // W446: la registrazione dell'azienda parte dalla Partita IVA, controllata dal server
  // (formato, già su Nexiora, VIES). Il resto del modulo compare quando la P.IVA è libera.
  const [pivaStato, setPivaStato] = useState(null); // null | "verifico" | risposta del server | {esito:"errore"}
  const [richiesta, setRichiesta] = useState(null); // null | {tipo, nome, email, telefono, messaggio, inviata}
  const pivaLibera = pivaStato?.esito === "libera";
  // W448: chi lavora in un'azienda già su Nexiora e non ha l'invito chiede l'accesso da qui
  const [accessoAz, setAccessoAz] = useState({ nome:"", email:"", messaggio:"", inviata:false });
  const legali = urlLegali(useFornitore());
  const { addToast } = useToast();
  const [resettingPassword, setResettingPassword] = useState(false);
  // W447: «Password dimenticata?» apre il riquadro di recupero; dopo l'invio resta il messaggio unico
  const [recuperoInviato, setRecuperoInviato] = useState(false);
  // Email già scritta tornando dalla pagina «Password aggiornata» (AzioneAccount)
  useEffect(() => {
    try { const e = sessionStorage.getItem("nexiora_email_accesso"); if (e) { sessionStorage.removeItem("nexiora_email_accesso"); setForm(f => ({ ...f, email: e })); } } catch { /* */ }
  }, []);

  // Arrivando dal link dentro l'email, l'invito è già noto — niente più codice illeggibile
  // da capire, niente più email da ridigitare a mano rischiando di sbagliarla: si legge
  // qui una volta sola (l'invito è pensato apposta per essere leggibile da chi non ha
  // ancora un account, con il nome dell'organizzazione già salvato dentro da chi ha creato
  // l'invito — vedi il commento in creaInvito) e si precompila tutto da solo.
  useEffect(() => {
    if (!invitoDaURL) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "inviti", invitoDaURL));
        if (snap.exists()) {
          const inv = snap.data();
          setInvitoInfo({ orgName: inv.organizationName || "", invitatoDaNome: inv.invitatoDaNome || "" });
          if (inv.emailDestinatario) setForm(f => ({ ...f, email: inv.emailDestinatario }));
        }
      } catch { /* niente di grave: resta il campo codice manuale come ripiego */ }
      setInvitoCaricato(true);
    })();
  }, [invitoDaURL]);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const verificaPiva = async () => {
    const errFormato = erroreFormatoPiva(form.piva);
    if (errFormato) { setPivaStato({ esito: "non-valida", messaggio: errFormato }); return; }
    setPivaStato("verifico"); setRichiesta(null);
    try {
      const r = await verificaPivaRegistrazione(normalizzaPiva(form.piva));
      setPivaStato(r || { esito: "errore" });
      // dati dal VIES: si propongono, restano modificabili, non si sovrascrive quanto già scritto
      if (r?.esito === "libera" && r.vies?.stato === "valida")
        setForm(f => ({ ...f, orgName: f.orgName.trim() ? f.orgName : (r.vies.nome || ""), indirizzo: f.indirizzo.trim() ? f.indirizzo : (r.vies.indirizzo || "") }));
    } catch (e) {
      setPivaStato({ esito: "errore", messaggio: e?.code === "functions/resource-exhausted" ? e.message : "Verifica non riuscita per un problema di connessione: riprova." });
    }
  };

  const inviaRichiesta = async () => {
    if (!richiesta?.nome?.trim() || !/^\S+@\S+\.\S+$/.test(richiesta?.email || "")) { addToast("Scrivi nome e cognome e un'email valida", "error"); return; }
    setLoading(true);
    try {
      await richiediAccesso({ tipo: richiesta.tipo, piva: normalizzaPiva(form.piva), nome: richiesta.nome, email: richiesta.email, telefono: richiesta.telefono || "", messaggio: richiesta.messaggio || "" });
      setRichiesta(r => ({ ...r, inviata: true }));
    } catch (e) {
      addToast(e?.message && !/internal/i.test(e.message) ? e.message : "Invio non riuscito: riprova tra poco.", "error");
    }
    setLoading(false);
  };

  // Password dimenticata — usa lo stesso campo email già presente nel form di login.
  // Il messaggio di conferma è SEMPRE lo stesso, email registrata o no (vedi resetPassword
  // in AuthContext): altrimenti basterebbe provare indirizzi a caso per scoprire quali
  // email sono già su Nexiora.
  const handleForgotPassword = async () => {
    const email = form.email.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) { addToast("Scrivi l'indirizzo email del tuo account", "error"); return; }
    setResettingPassword(true);
    try {
      await resetPassword(email);
      setRecuperoInviato(true);
    } catch (err) {
      if (err.code === "auth/invalid-email") addToast("Questo indirizzo email non è scritto in modo corretto", "error");
      else if (err.code === "auth/too-many-requests") addToast(err.message, "error", 8000);
      else setRecuperoInviato(true);
    }
    setResettingPassword(false);
  };


  const submit = async e => {
    e.preventDefault();
    if (mode === "recupero") { await handleForgotPassword(); return; }
    if (mode === "chiedi-accesso") {
      const errP = erroreFormatoPiva(form.piva);
      if (errP) { addToast(errP, "error"); return; }
      if (!accessoAz.nome.trim() || !/^\S+@\S+\.\S+$/.test(accessoAz.email.trim())) { addToast("Scrivi nome e cognome e un'email valida", "error"); return; }
      setLoading(true);
      try {
        await richiediAccesso({ tipo: "accesso", piva: normalizzaPiva(form.piva), nome: accessoAz.nome.trim(), email: accessoAz.email.trim(), telefono: "", messaggio: accessoAz.messaggio.trim() });
        setAccessoAz(a => ({ ...a, inviata: true }));
      } catch (er) { addToast(er?.message && !/internal/i.test(er.message) ? er.message : "Invio non riuscito: riprova tra poco.", "error"); }
      setLoading(false);
      return;
    }
    if (mode === "register-org" && !pivaLibera) {
      addToast("Prima verifica la Partita IVA dell'azienda", "error"); return;
    }
    if (mode === "register-org" || mode === "register-member") {
      const errPwd = erroreNuovaPassword(form.password, form.email);
      if (errPwd) { addToast(errPwd, "error"); return; }
    }
    if (mode !== "login" && form.password !== form.confirmPassword) {
      addToast("Le password non coincidono", "error"); return;
    }
    if (mode !== "login" && (!form.nome.trim() || !form.cognome.trim())) {
      addToast("Inserisci nome e cognome", "error"); return;
    }
    if (mode === "register-org" && (!accetta.condizioni || !accetta.clausole)) {
      addToast("Per registrare l'azienda devi accettare le Condizioni di servizio e approvare le clausole indicate", "error"); return;
    }
    if (mode === "register-member" && !accetta.privacy) {
      addToast("Conferma di aver letto l'Informativa privacy", "error"); return;
    }
    const nomeCompleto = `${form.nome.trim()} ${form.cognome.trim()}`.trim();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else if (mode === "register-org") {
        if (!form.orgName.trim()) { addToast("Inserisci la ragione sociale", "error"); setLoading(false); return; }
        if (!form.indirizzo.trim()) { addToast("Inserisci l'indirizzo della sede", "error"); setLoading(false); return; }
        const confermato = await confirm(
          `Stai per registrare "${form.orgName.trim()}" su Nexiora come Titolare — avrai accesso pieno e amministrativo. Procedi solo se sei autorizzato a farlo per conto dell'azienda.`,
          { type: "warning" }
        );
        if (!confermato) { setLoading(false); return; }
        await registerOrganization({
          piva: normalizzaPiva(form.piva), ragioneSociale: form.orgName.trim(), indirizzo: form.indirizzo.trim(),
          nome: form.nome.trim(), cognome: form.cognome.trim(), email: form.email.trim().toLowerCase(), password: form.password,
          telefono: form.phone.trim(), versione: VERSIONE_DOCUMENTI_LEGALI, condizioni: accetta.condizioni, clausole: accetta.clausole,
        });
        addToast("Azienda registrata! Ti abbiamo mandato un'email: apri il link per confermare il tuo indirizzo.", "success", 9000);
      } else {
        const risultato = await registerMember(form.email, form.password, nomeCompleto, form.inviteCode, form.phone, { versione: VERSIONE_DOCUMENTI_LEGALI });
        addToast(risultato.accessoImmediato
          ? "Benvenuto in Nexiora! Il tuo accesso è già attivo."
          : "Registrazione completata! L'amministratore deve ancora scegliere il tuo ruolo: riceverai l'accesso a breve.", "info", 9000);
        if (!risultato.accessoImmediato) setMode("login");
      }
    } catch(err) {
      if (mode === "register-org" && err.codice === "registrata") { setPivaStato({ esito: "registrata" }); setLoading(false); return; }
      let msg = err.message;
      if (mode === "register-org" && /^internal$/i.test(msg || "")) msg = "REGISTRAZIONE_INCOMPLETA";
      if (msg.includes("invalid-credential") || msg.includes("wrong-password") || msg.includes("user-not-found")) msg = "Email o password errati";
      if (msg.includes("email-already-in-use")) msg = "Email già registrata";
      if (msg.includes("weak-password")) msg = "Password troppo debole: almeno 8 caratteri";
      if (msg.includes("too-many-requests")) msg = "Troppi tentativi di accesso: riprova tra qualche minuto, oppure reimposta la password con «Password dimenticata?».";
      if (msg.includes("network-request-failed")) msg = "Connessione assente: controlla la rete e riprova.";
      if (msg.includes("user-disabled")) msg = "Questo account è stato disattivato: chiedi al titolare della tua azienda.";
      if (msg === "INVITO_REVOCATO") msg = "Questo invito è stato revocato. Chiedi all'amministratore un nuovo invito.";
      if (msg === "INVITO_GIA_USATO") msg = "Questo invito è già stato utilizzato. Se pensi sia un errore, contatta l'amministratore.";
      if (msg === "INVITO_SCADUTO") msg = "Questo invito è scaduto. Chiedi all'amministratore un nuovo invito.";
      if (msg === "INVITO_EMAIL_NON_CORRISPONDENTE") msg = "Questo invito è stato inviato a un altro indirizzo email. Usa l'email a cui è stato inviato l'invito.";
      if (msg === "CODICE_NON_VALIDO") msg = "Codice invito non riconosciuto. Apri il link dell'email, o chiedi all'amministratore di mandarti un nuovo invito.";
      if (msg === "REGISTRAZIONE_INCOMPLETA") msg = "La registrazione non è andata a buon fine per un problema temporaneo — riprova, il tuo indirizzo email è di nuovo disponibile.";
      addToast(msg, "error");
    }
    setLoading(false);
  };

  return (
    <div className="login-page-wrapper" style={{ height:"100vh", overflow:"hidden", display:"flex", justifyContent:"center", background:"linear-gradient(135deg,#0f0c29,#302b63,#24243e)" }}>
      {/* Pattern geometrico sfondo */}
      <div style={{position:"fixed",inset:0,overflow:"hidden",pointerEvents:"none",zIndex:0}}>
        {[...Array(6)].map((_,i)=>(
          <div key={i} style={{position:"absolute",borderRadius:"50%",border:"1px solid rgba(124,58,237,.15)",
            width:200+i*120,height:200+i*120,
            top:"50%",left:"30%",transform:"translate(-50%,-50%)",opacity:1-i*.15}}/>
        ))}
      </div>

      {/* Left panel — branding.
          FIX 12/09: era flex:1 puro, quindi su un monitor molto largo si allargava senza
          limite, spingendo il form (larghezza fissa) verso il bordo destro invece di
          restare al centro dello schermo. Con un tetto massimo, sopra una certa larghezza
          l'intero gruppo (i tre pannelli insieme) si centra da solo grazie al
          justifyContent:"center" messo sul contenitore esterno — lo stesso sfondo scuro
          già presente lì riempie lo spazio avanzato ai lati, senza vuoti bianchi. Sotto
          quella soglia (la stragrande maggioranza degli schermi) il comportamento resta
          identico a prima. */}
      <div className="login-left-panel" style={{flex:"1 1 auto",maxWidth:640,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:60,position:"relative",zIndex:1}}>
        <svg className="login-logo" width="80" height="80" viewBox="0 0 72 72" style={{marginBottom:24}}>
          <defs>
            <linearGradient id="lg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a78bfa"/>
              <stop offset="100%" stopColor="#7c3aed"/>
            </linearGradient>
          </defs>
          <polygon points="36,2 70,36 36,70 2,36" fill="url(#lg)"/>
          <polygon points="36,12 60,36 36,60 12,36" fill="none" stroke="white" strokeWidth="1.5" opacity="0.3"/>
          <line x1="22" y1="24" x2="22" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
          <line x1="50" y1="24" x2="50" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
          <line x1="22" y1="24" x2="50" y2="48" stroke="white" strokeWidth="4.5" strokeLinecap="round"/>
        </svg>
        <h1 className="login-title" style={{fontSize:42,fontWeight:800,color:"#fff",letterSpacing:"-1px",marginBottom:8}}>Nexiora</h1>
        <p className="login-subtitle" style={{fontSize:16,color:"rgba(255,255,255,.5)",marginBottom:48,textAlign:"center"}}>Piattaforma Operativa Aziendale</p>
        <div className="login-feature-list" style={{display:"flex",flexDirection:"column",gap:16,maxWidth:320,width:"100%"}}>
          {[
            {emoji:"📁",testo:"Gestione commesse e clienti"},
            {emoji:"👥",testo:"Capacità produttiva e presenze"},
            {emoji:"📊",testo:"Controllo economico in tempo reale"},
            {emoji:"🔮",testo:"Nexiora Insight — decisioni consapevoli"},
          ].map(({emoji,testo})=>(
            <div key={testo} style={{display:"flex",alignItems:"center",gap:12,color:"rgba(255,255,255,.7)",fontSize:13}}>
              <span style={{fontSize:18}}>{emoji}</span>{testo}
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — form.
          FIX 09/09: era minHeight:"100vh" — con il body globale a height:100vh e
          overflow:hidden (pensato per l'app vera, non per questa pagina), un modulo
          compilato più alto dello schermo restava tagliato invece che scorrere: il
          pannello "cresceva" oltre i 100vh invece di restare fermo e scorrere al suo
          interno. Con height fissa a 100vh, l'overflowY:"auto" già presente ora funziona
          davvero — il pulsante resta sempre raggiungibile scorrendo, su qualunque
          risoluzione, senza dover ridurre lo zoom del browser. */}
      <div className="login-right-panel" style={{width:440,background:"#fff",display:"flex",flexDirection:"column",alignItems:"center",padding:"26px 48px 0",position:"relative",zIndex:1,height:"100vh",overflow:"hidden"}}>
        {/* Zona che scorre: tutto tranne il pulsante finale, così il pulsante resta sempre
            visibile in fondo invece di sparire in coda a un form lungo. minHeight:0 è
            necessario qui: senza, un figlio flex non si restringe mai e overflowY:"auto"
            non avrebbe mai occasione di attivarsi. */}
        {/* justifyContent "safe center": centra verticalmente quando il contenuto ci sta
            (form di login, corto), ma su un form lungo (registrazione azienda, 8 campi)
            la parola "safe" evita un problema noto di CSS — senza, un contenuto più alto
            dello spazio disponibile centrato e con scroll può risultare tagliato in alto,
            costringendo a scorrere "all'indietro" per vederne l'inizio. */}
        <div style={{ width:"100%", flex:1, minHeight:0, overflowY:"auto", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"safe center" }} className="login-form-scroll">

        <div style={{ width:"100%", maxWidth:420 }}>
          {/* Tabs */}
          {mode === "login" && (
            <div style={{ display:"flex", gap:8, marginBottom:20 }}>
              <button onClick={() => setMode("register-org")} style={{ flex:1, padding:"10px", border:"2px solid #e5e7eb", borderRadius:10, background:"#fff", cursor:"pointer", fontSize:13, fontWeight:500, fontFamily:"'Inter',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:6, color:"#374151", transition:"all 0.15s" }}
                onMouseOver={e => e.currentTarget.style.borderColor = "#7c3aed"}
                onMouseOut={e => e.currentTarget.style.borderColor = "#e5e7eb"}>
                <Building2 size={15}/> Registra azienda
              </button>
              <button onClick={() => setMode("register-member")} style={{ flex:1, padding:"10px", border:"2px solid #e5e7eb", borderRadius:10, background:"#fff", cursor:"pointer", fontSize:13, fontWeight:500, fontFamily:"'Inter',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:6, color:"#374151", transition:"all 0.15s" }}
                onMouseOver={e => e.currentTarget.style.borderColor = "#7c3aed"}
                onMouseOut={e => e.currentTarget.style.borderColor = "#e5e7eb"}>
                <User size={15}/> Sono già invitato
              </button>
            </div>
          )}

          {/* Card */}
          <div style={{ background:"#fff", borderRadius:14, padding:28, border:"1px solid #e5e7eb", boxShadow:"0 4px 20px rgba(0,0,0,0.06)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
              <h2 style={{ fontSize:17, fontWeight:700 }}>
                {mode === "login" ? "Accedi" : mode === "recupero" ? "Recupera la password" : mode === "chiedi-accesso" ? "Chiedi l'accesso" : mode === "register-org" ? "Registra la tua azienda" : "Unisciti al team"}
              </h2>
              {/* Solo per chi si unisce con un invito: spesso ha già un account e può
                  essersi sbagliato di form. Non su "Registra la tua azienda" — chi arriva
                  lì è quasi sempre un cliente nuovo alla prima registrazione vera, un link
                  "Accedi" proprio in quel riquadro lo porterebbe fuori strada per errore. */}
              {(mode === "register-member" || mode === "recupero" || mode === "chiedi-accesso") && (
                <button onClick={() => { setMode("login"); setRecuperoInviato(false); setAccessoAz(a => ({ ...a, inviata:false })); }} style={{ fontSize:13, color:"#7c3aed", background:"none", border:"none", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                  ← Accedi
                </button>
              )}
            </div>

            <form id="loginForm" onSubmit={submit}>
              {mode === "register-org" && !pivaStato && (
                <div data-test="chi-registra" style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:8, padding:"9px 12px", marginBottom:14, fontSize:12.5, color:"#4c1d95", lineHeight:1.55 }}>
                  Registra l'azienda se ne sei il titolare o sei autorizzato a farlo: diventerai tu il titolare su Nexiora.
                  <div style={{ marginTop:4 }}>Vuoi solo vedere com'è? <a href="https://nexiora.it/tour.html" target="_blank" rel="noreferrer" style={linkInline}>Guarda come funziona</a> · Lavori in un'azienda già su Nexiora? <button type="button" onClick={() => setMode("chiedi-accesso")} style={linkInline}>Chiedi l'accesso</button></div>
                </div>
              )}
              {mode === "register-org" && (
                <div className="form-group" data-test="piva-registrazione">
                  <label className="form-label" style={{fontSize:13,textTransform:"none",letterSpacing:0,color:"#374151",fontWeight:700}}>Partita IVA dell'azienda *</label>
                  <div style={{ display:"flex", gap:8 }}>
                    <input className="input" name="piva" value={form.piva} maxLength={20} placeholder="Es. 01234567890"
                      onChange={e => { handle(e); setPivaStato(null); setRichiesta(null); }}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); verificaPiva(); } }}
                      style={{ fontSize:15, fontWeight:500 }}/>
                    <button type="button" onClick={verificaPiva} disabled={pivaStato === "verifico" || !form.piva.trim()}
                      style={{ padding:"0 14px", background: pivaLibera ? "#f0fdf4" : "#7c3aed", color: pivaLibera ? "#166534" : "#fff", border: pivaLibera ? "1px solid #bbf7d0" : "none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                      {pivaStato === "verifico" ? "Verifico…" : pivaLibera ? "✓ Verificata" : "Verifica"}
                    </button>
                  </div>
                  {!pivaStato && <div style={{fontSize:12,color:"#9ca3af",marginTop:4}}>Le ditte individuali senza P.IVA numerica possono usare il Codice Fiscale. Ogni azienda si registra una volta sola.</div>}
                  {(pivaStato?.esito === "non-valida" || pivaStato?.esito === "errore") && (
                    <div role="alert" style={{ fontSize:12.5, color:"#b91c1c", marginTop:6 }}>{pivaStato.messaggio || "Partita IVA non valida: controlla di averla scritta bene."}</div>
                  )}
                  {pivaLibera && (
                    <div style={{ fontSize:12.5, marginTop:6, lineHeight:1.5, color: pivaStato.vies?.stato === "valida" ? "#166534" : "#92400e" }}>
                      {pivaStato.vies?.stato === "valida" ? "✓ Trovata nel VIES (archivio europeo delle partite IVA): ragione sociale e sede sono già scritte qui sotto, correggile se serve."
                        : pivaStato.vies?.stato === "non-iscritta" ? "Partita IVA valida ma non presente nel VIES (succede a chi non è iscritto alle operazioni con l'estero): scrivi tu ragione sociale e sede."
                        : pivaStato.vies?.stato === "non-applicabile" ? "Codice Fiscale valido: scrivi tu ragione sociale e sede."
                        : "Partita IVA valida. Il servizio europeo VIES in questo momento non risponde: scrivi tu ragione sociale e sede, verranno ricontrollate."}
                    </div>
                  )}
                  {pivaStato?.esito === "registrata" && (
                    <div role="alert" style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:"10px 12px", marginTop:8, fontSize:12.5, color:"#9a3412", lineHeight:1.5 }}>
                      <strong>Questa Partita IVA è già su Nexiora.</strong>
                      {!richiesta && <>
                        <div style={{ marginTop:4 }}>Se lavori in quell'azienda, chiedi l'accesso: decidono gli amministratori se invitarti.</div>
                        <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap" }}>
                          <button type="button" onClick={() => setRichiesta({ tipo:"accesso", nome:`${form.nome} ${form.cognome}`.trim(), email:form.email })} style={{ background:"#7c3aed", color:"#fff", border:"none", borderRadius:7, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Chiedi l'accesso</button>
                          <button type="button" onClick={() => setRichiesta({ tipo:"contestazione", nome:`${form.nome} ${form.cognome}`.trim(), email:form.email })} style={{ background:"#fff", color:"#9a3412", border:"1px solid #fed7aa", borderRadius:7, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>È la mia azienda e non l'ho registrata io</button>
                        </div>
                      </>}
                      {richiesta && !richiesta.inviata && (
                        <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:8 }}>
                          <div style={{ fontWeight:600 }}>{richiesta.tipo === "accesso" ? "Chiedi l'accesso agli amministratori dell'azienda" : "Segnalalo a Nexiora: verifichiamo con la visura camerale e ti ricontattiamo"}</div>
                          <input className="input" placeholder="Nome e cognome" value={richiesta.nome} onChange={e => setRichiesta(r => ({ ...r, nome:e.target.value }))}/>
                          <input className="input" type="email" placeholder="La tua email" value={richiesta.email} onChange={e => setRichiesta(r => ({ ...r, email:e.target.value }))}/>
                          {richiesta.tipo === "contestazione" && <input className="input" placeholder="Telefono (facoltativo)" value={richiesta.telefono || ""} onChange={e => setRichiesta(r => ({ ...r, telefono:e.target.value }))}/>}
                          <textarea className="input" rows={2} placeholder={richiesta.tipo === "accesso" ? "Messaggio (facoltativo): es. il tuo reparto" : "Messaggio (facoltativo)"} value={richiesta.messaggio || ""} onChange={e => setRichiesta(r => ({ ...r, messaggio:e.target.value }))}/>
                          <div style={{ display:"flex", gap:8 }}>
                            <button type="button" onClick={() => setRichiesta(null)} style={{ background:"#fff", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:7, padding:"6px 12px", fontSize:12, cursor:"pointer" }}>Annulla</button>
                            <button type="button" onClick={inviaRichiesta} disabled={loading} style={{ background:"#7c3aed", color:"#fff", border:"none", borderRadius:7, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>{loading ? "Invio…" : "Invia richiesta"}</button>
                          </div>
                        </div>
                      )}
                      {richiesta?.inviata && (
                        <div style={{ marginTop:6, color:"#166534", fontWeight:600 }}>
                          {richiesta.tipo === "accesso"
                            ? "✓ Richiesta inviata. Se ti conoscono, riceverai un'email di invito: aprila e sarai dentro."
                            : "✓ Segnalazione ricevuta. Nexiora verifica con la visura camerale e ti ricontatta all'email indicata."}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {mode === "chiedi-accesso" ? (
                <div data-test="chiedi-accesso">
                  {accessoAz.inviata ? (
                    <div role="status" style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"12px 14px", fontSize:13, color:"#166534", lineHeight:1.55 }}>
                      <strong>Richiesta inviata.</strong> Se quella Partita IVA è su Nexiora, il titolare e gli amministratori ricevono il tuo nome e la tua email. Se ti conoscono, ti arriva un'email di invito: aprila e sei dentro.
                    </div>
                  ) : (<>
                    <p style={{ fontSize:13, color:"#4b5563", lineHeight:1.55, margin:"0 0 12px" }}>In un'azienda già su Nexiora si entra con l'invito del titolare o di un amministratore. Il modo più rapido è chiederlo a loro; altrimenti mandagli una richiesta da qui.</p>
                    <div className="form-group">
                      <label className="form-label" htmlFor="piva-accesso">Partita IVA dell'azienda</label>
                      <input id="piva-accesso" className="input" name="piva" value={form.piva} onChange={handle} placeholder="Es. 01234567890" maxLength={20}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="nome-accesso">Nome e cognome</label>
                      <input id="nome-accesso" className="input" value={accessoAz.nome} onChange={e => setAccessoAz(a => ({ ...a, nome:e.target.value }))} placeholder="Mario Rossi"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="email-accesso">La tua email</label>
                      <input id="email-accesso" className="input" type="email" autoComplete="email" value={accessoAz.email} onChange={e => setAccessoAz(a => ({ ...a, email:e.target.value }))} placeholder="nome@azienda.it"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="msg-accesso">Messaggio (facoltativo)</label>
                      <textarea id="msg-accesso" className="input" rows={2} value={accessoAz.messaggio} onChange={e => setAccessoAz(a => ({ ...a, messaggio:e.target.value }))} placeholder="Es. il tuo reparto o il cantiere"/>
                    </div>
                  </>)}
                </div>
              ) : mode === "recupero" ? (
                <div data-test="recupero-password">
                  {recuperoInviato ? (
                    <div role="status" style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"12px 14px", fontSize:13, color:"#166534", lineHeight:1.55 }}>
                      <strong>Controlla la tua email.</strong> Se <strong>{form.email.trim()}</strong> è registrato su Nexiora, tra poco ricevi un'email con il pulsante <strong>Reimposta password</strong>. Il link vale poco tempo e si usa una volta sola; se non arriva, guarda nello spam.
                    </div>
                  ) : (<>
                    <p style={{ fontSize:13, color:"#4b5563", lineHeight:1.55, margin:"0 0 14px" }}>Scrivi l'email del tuo account: ti mandiamo un link per scegliere una nuova password.</p>
                    <div className="form-group">
                      <label className="form-label" htmlFor="email-recupero">Email</label>
                      <input id="email-recupero" className="input" name="email" type="email" autoComplete="email" value={form.email} onChange={handle} placeholder="nome@azienda.it" required autoFocus/>
                    </div>
                  </>)}
                </div>
              ) : mode === "register-org" && !pivaLibera ? null : (<>
              {mode !== "login" && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  <div className="form-group">
                    <label className="form-label">Nome</label>
                    <input className="input" name="nome" value={form.nome} onChange={handle} placeholder="Mario" required/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cognome</label>
                    <input className="input" name="cognome" value={form.cognome} onChange={handle} placeholder="Rossi" required/>
                  </div>
                </div>
              )}
              {mode === "register-org" && (
                <>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:13,textTransform:"none",letterSpacing:0,color:"#374151",fontWeight:700}}>Ragione Sociale *</label>
                  <input className="input" name="orgName" value={form.orgName} onChange={handle} placeholder="Es. Rossi Impianti Srl, Studio Bianchi, ABC SpA..." required style={{fontSize:15,fontWeight:500}}/>
                  <div style={{fontSize:12,color:"#9ca3af",marginTop:4}}>Questo nome apparirà nell'app sotto il logo Nexiora</div>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{fontSize:13,textTransform:"none",letterSpacing:0,color:"#374151",fontWeight:700}}>Indirizzo sede *</label>
                  <input className="input" name="indirizzo" value={form.indirizzo} onChange={handle} placeholder="Es. Via Roma 1, 80100 Napoli" required/>
                </div>
                </>
              )}
              {mode === "register-member" && (
                invitoInfo ? (
                  // Arrivati dal link nell'email: l'invito è già verificato, il codice
                  // resta comunque nel form (invisibile) così l'invio funziona uguale —
                  // qui si mostra solo una conferma leggibile, mai la stringa del codice.
                  <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"12px 14px", marginBottom:14, fontSize:13, color:"#166534" }}>
                    ✓ Invito verificato{invitoInfo.invitatoDaNome ? ` — da ${invitoInfo.invitatoDaNome}` : ""}
                    {invitoInfo.orgName && <> per unirti a <strong>{invitoInfo.orgName}</strong></>}
                  </div>
                ) : invitoCaricato ? (
                  <div className="form-group">
                    <label className="form-label">Codice invito</label>
                    <input className="input" name="inviteCode" value={form.inviteCode} onChange={handle} placeholder="Il codice scritto in fondo all'email di invito" required/>
                    <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>Di solito basta aprire il link «Accetta l'invito» nell'email: il codice serve solo se il link non funziona.</div>
                  </div>
                ) : (
                  <div style={{ fontSize:13, color:"#9ca3af", marginBottom:14 }}>Verifica dell'invito in corso...</div>
                )
              )}
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="input" name="email" type="email" value={form.email} onChange={handle}
                  placeholder="nome@azienda.it" required
                  disabled={mode === "register-member" && !!invitoInfo}
                  style={mode === "register-member" && invitoInfo ? { background:"#f9fafb", color:"#6b7280" } : undefined}/>
                {mode === "register-member" && invitoInfo && (
                  <div style={{ fontSize:12, color:"#9ca3af", marginTop:4 }}>Email dell'invito — non modificabile</div>
                )}
              </div>
              {mode !== "login" && (
                <div className="form-group">
                  <label className="form-label">Telefono (opzionale)</label>
                  <input className="input" name="phone" value={form.phone} onChange={handle} placeholder="+39 333 0000000"/>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Password</label>
                <div style={{ position:"relative" }}>
                  <input className="input" name="password" type={showPass ? "text" : "password"} value={form.password} onChange={handle} autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder={mode === "login" ? "••••••••" : `Almeno ${PASSWORD_MIN} caratteri`} required style={{ paddingRight:40 }}/>
                  <button type="button" onClick={() => setShowPass(!showPass)} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#9ca3af" }}>
                    {showPass ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
                {mode === "login" && (
                  <button type="button" onClick={() => { setMode("recupero"); setRecuperoInviato(false); }}
                    style={{ marginTop:6, fontSize:12.5, color:"#7c3aed", background:"none", border:"none", cursor:"pointer", padding:0, fontFamily:"'Inter',sans-serif" }}>
                    Password dimenticata?
                  </button>
                )}
              </div>
              {mode !== "login" && (
                <div className="form-group">
                  <label className="form-label">Conferma Password</label>
                  <input className="input" name="confirmPassword" type="password" value={form.confirmPassword} onChange={handle} placeholder="••••••••" required/>
                </div>
              )}

              {mode === "register-org" && (
                <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:13, color:"#166534", lineHeight:1.5 }}>
                  🎁 3 mesi di prova con tutti i moduli, anche Gold. Nessuna carta richiesta. Poi da {euro(canoneMensile(LISTINO.bloccoPosti, "pro"))} al mese fino a {LISTINO.bloccoPosti} utenti.
                </div>
              )}
              {mode === "register-org" && (
                <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:14, fontSize:12.5, color:"#374151", lineHeight:1.5 }}>
                  <label style={{ display:"flex", gap:8, alignItems:"flex-start", cursor:"pointer" }}>
                    <input type="checkbox" checked={accetta.condizioni} onChange={e=>setAccetta(a=>({...a, condizioni:e.target.checked}))} style={{ marginTop:3, flexShrink:0 }}/>
                    <span>Dichiaro di agire per conto dell'azienda e accetto le <a href={legali.condizioni} target="_blank" rel="noreferrer" style={{color:"#7c3aed",fontWeight:600}}>Condizioni di servizio</a>. Ho letto l'<a href={legali.privacy} target="_blank" rel="noreferrer" style={{color:"#7c3aed",fontWeight:600}}>Informativa privacy</a>.</span>
                  </label>
                  <label style={{ display:"flex", gap:8, alignItems:"flex-start", cursor:"pointer" }}>
                    <input type="checkbox" checked={accetta.clausole} onChange={e=>setAccetta(a=>({...a, clausole:e.target.checked}))} style={{ marginTop:3, flexShrink:0 }}/>
                    <span>Ai sensi degli artt. 1341 e 1342 c.c. approvo specificamente gli articoli 5, 6, 7, 8, 9, 10, 12, 14, 15, 16 e 17 delle Condizioni (fine della prova e sola lettura, conteggio utenti, sospensione, rinnovo e disdetta, variazione dei prezzi, modifiche del servizio, cancellazione per inattività, limitazioni di responsabilità, sub-responsabili, foro competente).</span>
                  </label>
                </div>
              )}
              {mode === "register-member" && (
                <label style={{ display:"flex", gap:8, alignItems:"flex-start", cursor:"pointer", marginBottom:12, fontSize:12.5, color:"#374151", lineHeight:1.5 }}>
                  <input type="checkbox" checked={accetta.privacy} onChange={e=>setAccetta(a=>({...a, privacy:e.target.checked}))} style={{ marginTop:3, flexShrink:0 }}/>
                  <span>Ho letto l'<a href={legali.privacy} target="_blank" rel="noreferrer" style={{color:"#7c3aed",fontWeight:600}}>Informativa privacy</a>.</span>
                </label>
              )}
              {mode === "register-member" && (
                <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:13, color:"#1d4ed8" }}>
                  ℹ️ Se l'invito indica già il tuo ruolo entri subito; altrimenti l'amministratore lo sceglie e ti dà l'accesso.
                </div>
              )}
              </>)}
            </form>
          </div>
          {mode === "login" && (
            <div data-test="altre-strade" style={{ display:"flex", flexDirection:"column", gap:4, margin:"16px 0 8px", fontSize:12.5, color:"#6b7280", textAlign:"center" }}>
              <span>Lavori in un'azienda che usa già Nexiora, ma non hai l'invito? <button type="button" onClick={() => setMode("chiedi-accesso")} style={linkInline}>Chiedi l'accesso</button></span>
              <span>Vuoi solo vedere com'è? <a href="https://nexiora.it/tour.html" target="_blank" rel="noreferrer" style={linkInline}>Guarda Nexiora sezione per sezione</a></span>
            </div>
          )}
        </div>
        </div>
        {/* Barra fissa col pulsante — fuori dalla zona che scorre qui sopra, quindi sempre
            visibile senza dover arrivare in fondo alla pagina. Il pulsante resta comunque
            collegato allo stesso form tramite l'attributo HTML form="loginForm": è lo
            stesso identico form di prima, cambia solo dove il pulsante è disegnato sullo
            schermo — submit, validazione e required continuano a funzionare esattamente
            come prima. */}
        <div style={{ width:"100%", maxWidth:420, padding:"12px 0 20px", flexShrink:0, background:"#fff", boxShadow:"0 -6px 16px rgba(0,0,0,0.06)" }} className="login-form-footer">
          {(mode === "recupero" && recuperoInviato) || (mode === "chiedi-accesso" && accessoAz.inviata) ? (
          <button type="button" onClick={() => { setMode("login"); setRecuperoInviato(false); setAccessoAz(a => ({ ...a, inviata:false })); }} style={{ width:"100%", padding:"11px", background:"#7c3aed", color:"#fff", border:"none", borderRadius:9, fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>Torna all'accesso</button>
          ) : (
          <button type="submit" form="loginForm" disabled={loading || resettingPassword || (mode === "register-org" && !pivaLibera)} style={{ width:"100%", padding:"11px", background:"#7c3aed", color:"#fff", border:"none", borderRadius:9, fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif", opacity:(loading || (mode === "register-org" && !pivaLibera)) ? 0.6 : 1, transition:"opacity 0.15s" }}>
            {loading || resettingPassword ? "Caricamento..." : mode === "login" ? "Accedi" : mode === "recupero" ? "Invia il link" : mode === "chiedi-accesso" ? "Invia la richiesta" : mode === "register-org" ? (pivaLibera ? "Registra l'azienda" : "Verifica prima la Partita IVA") : "Registrati"}
          </button>
          )}
        </div>
      </div>

      {/* Right panel - brand. Su schermi stretti si impila sotto gli altri due invece di
          sparire — vedi la regola dedicata in index.css. */}
      <div className="login-marketing-panel" style={{ width:420, background:"linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:48, color:"#fff" }}>
        <div style={{ fontSize:40, marginBottom:16 }}>🚀</div>
        <h2 style={{ fontSize:24, fontWeight:800, marginBottom:12, textAlign:"center", letterSpacing:"-0.5px" }}>
          Gestisci progetti come un professionista
        </h2>
        <p style={{ fontSize:15, opacity:0.85, textAlign:"center", lineHeight:1.6, marginBottom:32 }}>
          Nexiora è la piattaforma pensata per le aziende che lavorano a commessa. Gestisci progetti, clienti, programmazione settimanale, controllo economico e molto altro — tutto in un unico posto.
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:10, width:"100%" }}>
          {["✓ 3 mesi di prova con tutti i moduli", "✓ Nessuna carta richiesta", "✓ Commesse illimitate", "✓ Aggiornamenti continui"].map(f => (
            <div key={f} style={{ fontSize:14, opacity:0.9 }}>{f}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

const linkInline = { background:"none", border:"none", padding:0, color:"#7c3aed", fontWeight:600, cursor:"pointer", fontSize:"inherit", fontFamily:"inherit", textDecoration:"underline" };
